import React, {useState, useEffect} from 'react';
import useUserContext from '../common/UserContext';

import { apiGet, apiPost, getAccountsApiUrl, getAdminApiUrl, apiPatch, apiMessages } from '../common/AppApi';

import Processing from '../common/Processing';
import Pagination, { resetPaging } from '../common/Pagination';
import { formatPhone, cleanPhone, decodeValues } from '../common/Formats';
import { selectOptions } from '../common/Forms';
import { PageSize, PrivilegeCodes, StateArray } from '../common/Constants';
import MessageTag from '../common/MessageTag';
import Strings from '../common/Strings';

import AddRecipient from './AddRecipient';
import moment from 'moment';


export default function Applicants(props) {
	let {user} = useUserContext();
	let [programs, setPrograms] = useState([]);

	let [message, setMessage] = useState();
	let [showSpinner, setSpinner] = useState(false);
	let [dataRows, setDataRows] = useState([]);
	let [pagingData, setPagingData] = useState(resetPaging());
	let [errors, setErrors] = useState({});

	let getPrograms = () => {
		let url = getAccountsApiUrl() + '/application/programs';

		setSpinner(true);
		apiGet(url).then((resp) => {
			let programs = [];
			resp.forEach( (p) => {
				programs.push({id:p.programId, text:p.programName, uuid:p.programUuid});
			});
			setPrograms(programs);

			setSearchForm({...searchForm, programId:programs[0].id});

		}).catch( (err) => {
			console.error(err);
			let messageObject = {text:Strings.error.general,type:'danger'};
			if(err.privilegeError){
				messageObject.text = <>{err.message}</>;
			}
			setMessage(messageObject);
		}).finally(() => setSpinner(false));
	};

	useEffect(() => {
		getPrograms();
	}, []);

	let handleFieldChange = (event) => {
		const {id, value} = event.target;
		setSearchForm({...searchForm, [id]:value});
	}

	let emptyForm = {
		firstname:'',
		lastname:'',
		email:'',
		mobilePhone:'',
		programId:''
	};
	let [searchForm, setSearchForm] = useState(emptyForm);

	let disableSearch = () => {
		for(const searchField in searchForm){
			if(searchForm[searchField])
				return false;
		}
		return true;
	};

	let searchApplicants = (e, preserveErrors) => {
		e.preventDefault();

		if (!preserveErrors)
			setErrors({});

		setSpinner(true);

		let url = getAccountsApiUrl() + '/applicant';

		let queryParams = [];
		for(const searchField in searchForm) {
			if(searchField == 'mobilePhone') {
				queryParams.push(searchField + '=' + cleanPhone(searchForm[searchField]));
			} else {
				queryParams.push(searchField + '=' + encodeURIComponent(searchForm[searchField]));
			}
		}
		//TODO use backend paging?
		//queryParams.push('pageSize=10');
		if(queryParams.length)
			url = url + '?' + queryParams.join('&');

		apiGet(url).then((resp) => {
			//console.debug(resp);
			if (resp && resp.length) {
				setDataRows(resp);
				setMessage(null);
			} else {
				setDataRows([]);
				setMessage({text:'No applicants found.', type:'warning'});
			}
			setPagingData(resetPaging());
		}).catch( (err) => {
			console.error(err);
		}).finally( () => setSpinner(false) );
	};

	let [applicant, setApplicant] = useState({});
	let [selectedIds, setSelectedIds] = useState([]);

	useEffect(() => {
		setSelectedIds([]);
	}, [pagingData, dataRows]);

	let pageItems = () => {
		return dataRows.filter( (data, index) => (index >= pagingData.beginIndex) && (index < (pagingData.beginIndex + PageSize)) );
	};

	let toggleSelect = (applicantId) => {
		setSelectedIds( (prev) => prev.includes(applicantId) ? prev.filter( (id) => id != applicantId) : [...prev, applicantId] );
	};

	let toggleSelectAll = () => {
		let currentPageIds = pageItems().map( (item) => item.applicantId );
		let allSelected = currentPageIds.length > 0 && currentPageIds.every( (id) => selectedIds.includes(id) );
		setSelectedIds( allSelected ? [] : currentPageIds );
	};

	let buildRecipientBody = (item) => {
		return {
			recipientAccountIdentifier:'',
			firstname:item.firstname,
			lastname:item.lastname,
			middlename:item.middlename,
			address1:item.address1,
			address2:item.address2,
			city:item.city,
			stateCode:decodeValues(item.stateId, StateArray, 'id2', 'id'),
			zipcode:item.zipcode,
			email:item.email,
			mobilePhone:cleanPhone(item.mobilePhone),
			cardIssuanceCode:false,
			businessPhone:'',
			faxNumber:'',
			creationChannel:'API',
			recipientProgramIdentifier:decodeValues(item.programId, programs, null, 'uuid'),
			applicantId:item.applicantId
		};
	};

	let approveSelected = () => {
		if (!selectedIds.length)
			return;

		setSpinner(true);

		let url = getAccountsApiUrl() + '/account';
		let selectedItems = dataRows.filter( (item) => selectedIds.includes(item.applicantId) );

		Promise.allSettled( selectedItems.map( (item) => apiPost(url, buildRecipientBody(item)) ) ).then( (results) => {
			let successCount = results.filter( (r) => r.status == 'fulfilled' ).length;
			let failCount = results.length - successCount;

			let newErrors = {};
			results.forEach( (result, index) => {
				if (result.status == 'rejected') {
					let err = result.reason;
					let errorText = Strings.error.general;
					if (err && err.privilegeError) {
						errorText = err.message;
					} else if (err && err.status == 400) {
						errorText = apiMessages(err) || errorText;
					} else if (err && err.status == 409) {
						if (err.data && err.data.errorMessages && err.data.errorMessages.length && err.data.errorMessages[0].dataField == 'email') {
							errorText = 'Email already exists.';
						} else {
							errorText = apiMessages(err) || errorText;
						}
					}
					newErrors[selectedItems[index].applicantId] = errorText;
				}
			});

			if (failCount == 0) {
				setMessage({text:successCount + ' applicant(s) approved and converted to recipient.', type:'success'});
			} else {
				setMessage({text:successCount + ' applicant(s) approved. ' + failCount + ' failed — see error(s) below.', type: successCount ? 'warning' : 'danger'});
			}

			setErrors(newErrors);
			setSelectedIds([]);
			searchApplicants({preventDefault: () => {}}, true);
		}).finally( () => setSpinner(false) );
	};

	let ShowApplicants = () => {
		if (dataRows && dataRows.length) {

			let btnSmStyle = {padding:'.25rem .5rem', fontSize:'.875rem', lineHeight:'1.5', borderRadius:'.2rem', width:'9.5rem', textAlign:'center'};

			let addressOut = (addr2) => { return (addr2 ? <><br/>{addr2}</> : null); };

			let setupApplicant = (item) => {
				let appl = {...item};
				appl.stateCode = decodeValues(item.stateId, StateArray, 'id2', 'id');
				appl.programUuid = decodeValues(item.programId, programs, null, 'uuid');
				//console.debug(appl);
				setApplicant(appl);
			};

			let normalize = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');

			let dupKey = (item) => {
				let name = normalize(item.firstname) + ' ' + normalize(item.lastname);
				let address1 = normalize(item.address1);
				return name + '|' + address1;
			};

			let dupCounts = {};
			dataRows.forEach( (item) => {
				let key = dupKey(item);
				dupCounts[key] = (dupCounts[key] || 0) + 1;
			});

			const TableRow = (data) => {
				let item = data.row;
				let createdDate = moment(item.createdDate).format('YYYY-MM-DD HH:mm');
				let btnId = 'btn' + item.applicantId;
				let isDuplicate = dupCounts[dupKey(item)] > 1;
				let dupProps = isDuplicate ? {style:{backgroundColor:'#f0f0f0'}, title:'Potential Duplicate'} : {};
				let error = errors[item.applicantId];

				return <tr key={item.applicantId} {...dupProps}>
					<td><input type='checkbox' checked={selectedIds.includes(item.applicantId)} onChange={ () => toggleSelect(item.applicantId) } /></td>
					<td>{item.firstname} {item.middlename} {item.lastname}</td>
					<td>{item.address1}{ addressOut(item.address2) }<br/>{item.city} {decodeValues(item.stateId, StateArray, 'id2')} {item.zipcode}</td>
					<td>{item.email}</td>
					<td>{formatPhone(item.mobilePhone)}</td>
					<td>{createdDate}</td>
					<td>{ error ? <span className='text-danger'>{error}</span> : null }</td>
					<td><button type='button' id={btnId} className='btn btn-primary btn-sm' style={btnSmStyle} onClick={ () => setupApplicant(item) }>Process</button></td>
				</tr>
			};

			let tableRows = dataRows.map( (data, index) => {
				return ( (index >= pagingData.beginIndex) && (index < (pagingData.beginIndex + PageSize)) ) ? <TableRow key={index} row={data} /> : null;
			});

			let currentPageIds = pageItems().map( (item) => item.applicantId );
			let allPageSelected = currentPageIds.length > 0 && currentPageIds.every( (id) => selectedIds.includes(id) );

			return <>
			<br/>
			<table className='table'>
				<thead>
					<tr>
						<th style={{whiteSpace:'nowrap'}}><input type='checkbox' id='selectAllApplicants' checked={allPageSelected} onChange={toggleSelectAll} /> Select All</th>
						<th>Name</th><th>Address</th><th>Email</th><th>Mobile</th><th>Created</th><th>Status</th>
						<th><button type='button' id='btnApproveSelected' className='btn btn-success btn-sm' style={btnSmStyle} disabled={!selectedIds.length} onClick={approveSelected}>Approve Selected</button></th>
					</tr>
				</thead>
				<tbody>
					{ tableRows }
				</tbody>
			</table>

			<Pagination recordCount={dataRows.length} pagingData={pagingData} setPagingData={setPagingData} pageSize={PageSize} />

			</>;
		} else {
			return null;
		}
	};

	let divStyle = {
		width:'65rem', height:'47rem',
		margin: 'auto',
		position: 'fixed',
		top: 0, left: 0, bottom: 0, right: 0,
		zIndex: 100,
		overflowY:'scroll'
	};

	let cancelFunction = () => {
		setApplicant({});
	};

	let successFunction = () => {
		setApplicant({});
		let e = {preventDefault: () => console.log('search applicants') };
		searchApplicants(e);
	};

	let ProcessApplicant = (props) => {
		if (props.show) {
			return <div style={divStyle} id="addApplicant">
				<div className="card border-primary m-0">
					<div className="card-body">
						<AddRecipient cancelFunc={cancelFunction} applicant={applicant} successFunc={successFunction}/>
					</div>
				</div>
			</div>
		} else {
			return null;
		}
	};

	return (
		<>
		<h3>Applicants</h3>

		<MessageTag message={message} />
		<Processing show={showSpinner}/>

		<form onSubmit={searchApplicants}>
			<div className="row">
				<div className="form-group col-12 col-md-6 col-lg-3">
					<label htmlFor="programId">Program</label>
					<select id="programId" className="form-control custom-select" value={searchForm.programId} onChange={handleFieldChange} >
						{ selectOptions(programs) }
					</select>
				</div>
				<div className="form-group col-12 col-md-6 col-lg-3">
					<label htmlFor="firstname">First Name</label>
					<input type="text" id="firstname" className="form-control" onChange={handleFieldChange} value={searchForm.firstname} maxLength="100" />
				</div>
				<div className="form-group col-12 col-md-6 col-lg-3">
					<label htmlFor="lastname">Last Name</label>
					<input type="text" id="lastname" className="form-control" onChange={handleFieldChange} value={searchForm.lastname} maxLength="100" />
				</div>
			</div>
			<div className='row'>
				<div className="form-group col-12 col-md-6 col-lg-3">
					<label htmlFor="email">Email</label>
					<input type="text" id="email" className="form-control" onChange={handleFieldChange} value={searchForm.email} maxLength="255" />
				</div>
				<div className="form-group col-12 col-md-6 col-lg-3">
					<label htmlFor="mobilePhone">Phone</label>
					<input type="text" id="mobilePhone" className="form-control" onChange={handleFieldChange} value={searchForm.mobilePhone} maxLength="20" />
				</div>
			</div>
			<button className="btn btn-primary" disabled={false} title={Strings.warning.searchCritera} id="btnSearch">Search</button>
		</form>

		<ShowApplicants />

		<ProcessApplicant show={applicant.applicantId ? true : false}/>

		</>
	);
	
}
